import type { VideoShot } from '@cinematic-engine/video-dsl';
import type { SceneBlock } from './script-parser';

export type ShotEffectType = NonNullable<VideoShot['effects']>[number]['type'];

export interface ShotEffect {
  type: ShotEffectType;
  intensity: number;
}

export type TypographyStyle = NonNullable<VideoShot['typography']>[number]['style'];

export type TypographyAnimation = NonNullable<VideoShot['typography']>[number]['animation'];

export interface ShotPlan {
  sceneId: string;
  shotId: string;
  title: string;
  subtitle: string;
  purpose: string;
  emotion: string;
  energy: number;
  cameraMovement: CameraMovement;
  effects: ShotEffect[];
  typography: Array<{
    text: string;
    style: TypographyStyle;
    animation: TypographyAnimation;
    animationDuration: number;
    delay: number;
  }>;
}

export type CameraMovement =
  | 'slow_push'
  | 'slow_pull'
  | 'dolly'
  | 'truck'
  | 'orbit'
  | 'crane'
  | 'tilt'
  | 'pan'
  | 'rack_focus'
  | 'reveal'
  | 'handheld'
  | 'static';

export const ALL_CAMERA_MOVEMENTS: CameraMovement[] = [
  'slow_push',
  'slow_pull',
  'dolly',
  'truck',
  'orbit',
  'crane',
  'tilt',
  'pan',
  'rack_focus',
  'reveal',
  'handheld',
  'static',
];

export const ALL_EFFECT_TYPES: ShotEffectType[] = [
  'blur',
  'bloom',
  'dof',
  'grain',
  'vignette',
  'chromatic-aberration',
  'halation',
  'color-grade',
  'sharpen',
];

export const ALL_TYPOGRAPHY_STYLES: TypographyStyle[] = [
  'title',
  'subtitle',
  'chapter',
  'quote',
  'statistic',
  'lower-third',
  'caption',
  'credit',
];

export const ALL_TYPOGRAPHY_ANIMATIONS: TypographyAnimation[] = [
  'fade',
  'tracking',
  'mask-reveal',
  'word-reveal',
  'character-reveal',
  'scale',
  'blur',
  'clip',
];

export const VALID_EMOTIONS = ['suspense', 'joy', 'contemplation', 'wonder'] as const;

export type Emotion = (typeof VALID_EMOTIONS)[number];

export const CAMERA_MOVEMENTS: Record<'low' | 'medium' | 'high', CameraMovement[]> = {
  low: ['static', 'slow_push', 'pan'],
  medium: ['orbit', 'slow_push', 'dolly'],
  high: ['handheld', 'truck', 'dolly'],
};

const HIGH_ENERGY_WORDS = [
  'explos',
  'corre',
  'corre,',
  'fug',
  'confront',
  'luta',
  'batalha',
  'persegui',
  'pânico',
  'pânico',
  'alarme',
  'colis',
  'tiro',
  'arma',
  'grito',
  'corrida',
  'rápido',
  'veloz',
  'guerra',
  'invas',
];
const LOW_ENERGY_WORDS = [
  'silêncio',
  'silêncio',
  'paz',
  'calma',
  'reflex',
  'memória',
  'leito',
  'lento',
  'suave',
  'tarde',
  'por do sol',
  'pôr do sol',
  'sonho',
  'respira',
  'espera',
  'descanso',
  'vazio',
  'seren',
];
const SUSPENSE_WORDS = [
  'medo',
  'escuro',
  'noite',
  'sombra',
  'perigo',
  'ameaça',
  'mistério',
  'suspense',
  'gelo',
  'silêncio profundo',
];
const JOY_WORDS = [
  'sorri',
  'alegria',
  'celebra',
  'festa',
  'riso',
  'amanhecer',
  'vitória',
  'abraço',
  'encontro',
];

export function emotionForText(text: string): string {
  const lower = text.toLowerCase();
  if (SUSPENSE_WORDS.some((w) => lower.includes(w))) return 'suspense';
  if (JOY_WORDS.some((w) => lower.includes(w))) return 'joy';
  if (LOW_ENERGY_WORDS.some((w) => lower.includes(w))) return 'contemplation';
  return 'wonder';
}

export function energyForText(text: string): number {
  const lower = text.toLowerCase();
  const high = HIGH_ENERGY_WORDS.filter((w) => lower.includes(w)).length;
  const low = LOW_ENERGY_WORDS.filter((w) => lower.includes(w)).length;
  const score = high * 0.25 - low * 0.15;
  return Math.min(1, Math.max(0.15, 0.45 + score));
}

export function movementForEnergy(energy: number): CameraMovement {
  const bucket = energy >= 0.65 ? 'high' : energy >= 0.35 ? 'medium' : 'low';
  const pool = CAMERA_MOVEMENTS[bucket];
  return pool[((pool.length * energy) % pool.length) | 0];
}

export function effectsForScene(scene: SceneBlock, energy: number): ShotEffect[] {
  const emotion = emotionForText(`${scene.title} ${scene.lines.map((l) => l.text).join(' ')}`);
  const effects: ShotEffect[] = [{ type: 'color-grade', intensity: 0.5 }];
  if (emotion === 'suspense') effects.push({ type: 'vignette', intensity: 0.6 });
  if (emotion === 'contemplation') effects.push({ type: 'grain', intensity: 0.35 });
  if (energy >= 0.65) effects.push({ type: 'dof', intensity: 0.4 });
  return effects;
}

export function subtitleForScene(scene: SceneBlock): string {
  const dialogue = scene.lines.find((l) => l.kind === 'dialogue');
  if (dialogue) return dialogue.text.replace(/^[A-ZÀ-Ú0-9\s-]+[:(]/i, '').trim();
  const action = scene.lines.find((l) => l.kind === 'action' && l.text.length > 12);
  return action ? action.text.slice(0, 60) : '';
}

export function planScenes(scenes: SceneBlock[]): ShotPlan[] {
  return scenes.map((scene) => {
    const fullText = scene.lines.map((l) => l.text).join(' ');
    const energy = energyForText(fullText);
    return {
      sceneId: scene.id,
      shotId: `shot-${scene.id}`,
      title: sceneTitleOf(scene),
      subtitle: subtitleForScene(scene),
      purpose: `Estabelece ${sceneTitleOf(scene)}`,
      emotion: emotionForText(fullText),
      energy: Number(energy.toFixed(2)),
      cameraMovement: movementForEnergy(energy),
      effects: effectsForScene(scene, energy),
      typography: [
        {
          text: sceneTitleOf(scene).toUpperCase(),
          style: 'title',
          animation: 'mask-reveal',
          animationDuration: 1.5,
          delay: 0,
        },
      ],
    };
  });
}

function sceneTitleOf(scene: SceneBlock): string {
  return (
    scene.title
      .replace(/^(cena|scene)\s*\d*\s*[:.-]*\s*/i, '')
      .replace(/^(int\.|ext\.|int\.\/ext\.|int\/ext)[\s.:-]*/i, '') || `Cena ${scene.id}`
  );
}
